// src/features/dashboard/pages/AdminHomePage.tsx

import React, {
  useEffect,
  useState,
} from 'react';

import {
  Activity,
  Gamepad2,
  UserPlus,
  Users,
} from 'lucide-react';

import type {
  AdminDashboardData,
} from '../types/adminDashboard.types';

import AdminSummaryCard
  from '../components/AdminSummaryCard';

import AdminTaskSection
  from '../components/AdminTaskSection';

import CustomerStatusSection
  from '../components/CustomerStatusSection';

import TodayGameSection
  from '../components/TodayGameSection';

import VisitorTrendSection
  from '../components/VisitorTrendSection';


const mockDashboard:
  AdminDashboardData = {
    summary: {
      todayVisitors: 24,
      todayNewVisitors: 5,
      todayGameParticipants: 31,
      runningGames: 1,
    },

    todayGames: [
      {
        gameId: 1,
        gameTitle:
          '초보자 Daily',
        gameTime:
          '19:00',
        participantCount: 8,
        status:
          'WAIT',
      },

      {
        gameId: 2,
        gameTitle:
          'Mystery Bounty',
        gameTime:
          '20:30',
        participantCount: 17,
        status:
          'RUNNING',
      },

      {
        gameId: 3,
        gameTitle:
          'Satellite',
        gameTime:
          '22:00',
        participantCount: 6,
        status:
          'WAIT',
      },
    ],

    customerStatus: {
      recent7Days: 74,
      active30Days: 126,
      newUsers: 9,
      inactive14Days: 21,
    },

    visitorTrend: [
      {
        date: '월',
        count: 18,
      },
      {
        date: '화',
        count: 21,
      },
      {
        date: '수',
        count: 17,
      },
      {
        date: '목',
        count: 24,
      },
      {
        date: '금',
        count: 31,
      },
      {
        date: '토',
        count: 38,
      },
      {
        date: '일',
        count: 29,
      },
    ],

    tasks: {
      revisitTargetCount: 8,
      pendingPointCount: 2,
    },
  };


export default function AdminDashboardPage():
  React.JSX.Element {
  const [
    dashboard,
    setDashboard,
  ] =
    useState<
      AdminDashboardData | null
    >(null);


  const [
    loading,
    setLoading,
  ] =
    useState(true);


  useEffect(
    () => {
      /**
       * UI 개발 단계
       *
       * 추후 dashboardService 연결
       */
      setDashboard(
        mockDashboard,
      );

      setLoading(
        false,
      );
    },
    [],
  );


  /**
   * =========================================================
   * Loading
   * =========================================================
   */
  if (
    loading ||
    !dashboard
  ) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">

        <div className="flex flex-col items-center gap-3">

          <span
            className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-main"
            aria-hidden="true"
          />


          <p className="text-sm text-gray-500">
            매장 현황을 불러오고 있습니다.
          </p>

        </div>

      </div>
    );
  }


  return (
    <main
      className="
        min-h-full
        bg-gray-50
        px-3
        pb-8
        pt-4

        sm:px-5
        sm:pb-10
        sm:pt-6

        lg:px-7

        xl:px-8
      "
    >

      <div className="mx-auto w-full max-w-[1440px]">

        {/* =================================================
            Header
            ================================================= */}
        <header
          className="
            mb-5

            sm:mb-6

            lg:mb-7
          "
        >

          <p
            className="
              text-[10px]
              font-bold
              tracking-[0.16em]
              text-main

              sm:text-xs
              sm:tracking-[0.18em]
            "
          >
            STORE DASHBOARD
          </p>


          <h1
            className="
              mt-1
              text-xl
              font-bold
              tracking-tight
              text-gray-900

              sm:text-2xl

              lg:text-3xl
            "
          >
            오늘의 매장 현황
          </h1>


          <p
            className="
              mt-1
              max-w-2xl
              text-xs
              leading-5
              text-gray-500

              sm:text-sm
            "
          >
            오늘 매장 운영에 필요한 주요 정보를 확인할 수 있습니다.
          </p>

        </header>


        {/* =================================================
            Summary

            Mobile
            1열

            sm
            2열

            xl
            4열
            ================================================= */}
        <section
          className="
            grid
            grid-cols-1
            gap-3

            sm:grid-cols-2
            sm:gap-4

            xl:grid-cols-4
          "
        >

          <AdminSummaryCard
            title="오늘 방문"
            value={
              dashboard
                .summary
                .todayVisitors
            }
            suffix="명"
            icon={
              <Users
                size={20}
              />
            }
          />


          <AdminSummaryCard
            title="신규 방문"
            value={
              dashboard
                .summary
                .todayNewVisitors
            }
            suffix="명"
            icon={
              <UserPlus
                size={20}
              />
            }
          />


          <AdminSummaryCard
            title="게임 참가"
            value={
              dashboard
                .summary
                .todayGameParticipants
            }
            suffix="명"
            icon={
              <Gamepad2
                size={20}
              />
            }
          />


          <AdminSummaryCard
            title="진행 게임"
            value={
              dashboard
                .summary
                .runningGames
            }
            suffix="개"
            icon={
              <Activity
                size={20}
              />
            }
          />

        </section>


        {/* =================================================
            Main Section

            Mobile ~ LG
            1열

            XL 이상
            2열
            ================================================= */}
        <section
          className="
            mt-5
            grid
            grid-cols-1
            gap-4

            sm:mt-6
            sm:gap-5

            xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,1fr)]
            xl:gap-6
          "
        >

          <div className="min-w-0">
            <TodayGameSection
              games={
                dashboard
                  .todayGames
              }
            />
          </div>


          <div className="min-w-0">
            <CustomerStatusSection
              data={
                dashboard
                  .customerStatus
              }
            />
          </div>

        </section>


        {/* =================================================
            Bottom Section

            Mobile ~ LG
            1열

            XL 이상
            2열
            ================================================= */}
        <section
          className="
            mt-4
            grid
            grid-cols-1
            gap-4

            sm:mt-5
            sm:gap-5

            xl:mt-6
            xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,1fr)]
            xl:gap-6
          "
        >

          <div className="min-w-0">
            <VisitorTrendSection
              data={
                dashboard
                  .visitorTrend
              }
            />
          </div>


          <div className="min-w-0">
            <AdminTaskSection
              data={
                dashboard
                  .tasks
              }
            />
          </div>

        </section>

      </div>

    </main>
  );
}